import { useState, useCallback, useEffect } from 'react';

const MIN_ZOOM_LEVEL = 1;
const MAX_ZOOM_LEVEL = 14; // ~2 hours visible for a 7-day event
const ZOOM_FACTOR = 1.3;

export interface TimeBounds {
  start: Date;
  end: Date;
}

export interface UseTimelineViewportParams {
  getEventBounds: () => TimeBounds | null;
  eventKey?: string; // When this changes, reset view to full event
}

export interface UseTimelineViewportReturn {
  viewBounds: TimeBounds | null;
  zoomIn: (centerRatio?: number) => void;
  zoomOut: (centerRatio?: number) => void;
  panLeft: () => void;
  panRight: () => void;
  resetView: () => void;
  setViewBounds: React.Dispatch<React.SetStateAction<TimeBounds | null>>;
  canZoomIn: boolean;
  canZoomOut: boolean;
  canPanLeft: boolean;
  canPanRight: boolean;
  isZoomed: boolean;
}

export function useTimelineViewport(
  params: UseTimelineViewportParams
): UseTimelineViewportReturn {
  const { getEventBounds, eventKey = '' } = params;
  const [viewBounds, setViewBounds] = useState<TimeBounds | null>(null);

  // Reset view to full event bounds when event changes
  useEffect(() => {
    const bounds = getEventBounds();
    if (bounds) {
      setViewBounds({ start: bounds.start, end: bounds.end });
    } else {
      setViewBounds(null);
    }
  }, [eventKey, getEventBounds]);

  const zoomIn = useCallback(
    (centerRatio = 0.5) => {
      const bounds = getEventBounds();
      const current = viewBounds ?? bounds;
      if (!bounds || !current) return;

      const eventDuration = bounds.end.getTime() - bounds.start.getTime();
      const viewDuration = current.end.getTime() - current.start.getTime();
      const zoomLevel = eventDuration / viewDuration;
      if (zoomLevel >= MAX_ZOOM_LEVEL) return;

      const newViewDuration = viewDuration / ZOOM_FACTOR;
      const centerTime =
        current.start.getTime() + viewDuration * centerRatio;
      let newStart = centerTime - newViewDuration * centerRatio;
      let newEnd = newStart + newViewDuration;

      if (newStart < bounds.start.getTime()) {
        newStart = bounds.start.getTime();
        newEnd = newStart + newViewDuration;
      }
      if (newEnd > bounds.end.getTime()) {
        newEnd = bounds.end.getTime();
        newStart = newEnd - newViewDuration;
      }

      setViewBounds({
        start: new Date(newStart),
        end: new Date(newEnd),
      });
    },
    [getEventBounds, viewBounds]
  );

  const zoomOut = useCallback(
    (centerRatio = 0.5) => {
      const bounds = getEventBounds();
      const current = viewBounds ?? bounds;
      if (!bounds || !current) return;

      const eventDuration = bounds.end.getTime() - bounds.start.getTime();
      const viewDuration = current.end.getTime() - current.start.getTime();
      const zoomLevel = eventDuration / viewDuration;
      if (zoomLevel <= MIN_ZOOM_LEVEL) return;

      const newViewDuration = Math.min(
        viewDuration * ZOOM_FACTOR,
        eventDuration
      );
      const centerTime =
        current.start.getTime() + viewDuration * centerRatio;
      let newStart = centerTime - newViewDuration * centerRatio;
      let newEnd = newStart + newViewDuration;

      if (newStart < bounds.start.getTime()) {
        newStart = bounds.start.getTime();
        newEnd = newStart + newViewDuration;
      }
      if (newEnd > bounds.end.getTime()) {
        newEnd = bounds.end.getTime();
        newStart = newEnd - newViewDuration;
      }

      setViewBounds({
        start: new Date(newStart),
        end: new Date(newEnd),
      });
    },
    [getEventBounds, viewBounds]
  );

  const PAN_FRACTION = 0.25;

  const panLeft = useCallback(() => {
    const bounds = getEventBounds();
    const current = viewBounds ?? bounds;
    if (!bounds || !current) return;

    const viewDur = current.end.getTime() - current.start.getTime();
    const shift = viewDur * PAN_FRACTION;
    let newStart = current.start.getTime() - shift;
    let newEnd = current.end.getTime() - shift;

    if (newStart < bounds.start.getTime()) {
      newStart = bounds.start.getTime();
      newEnd = newStart + viewDur;
    }

    setViewBounds({ start: new Date(newStart), end: new Date(newEnd) });
  }, [getEventBounds, viewBounds]);

  const panRight = useCallback(() => {
    const bounds = getEventBounds();
    const current = viewBounds ?? bounds;
    if (!bounds || !current) return;

    const viewDur = current.end.getTime() - current.start.getTime();
    const shift = viewDur * PAN_FRACTION;
    let newStart = current.start.getTime() + shift;
    let newEnd = current.end.getTime() + shift;

    if (newEnd > bounds.end.getTime()) {
      newEnd = bounds.end.getTime();
      newStart = newEnd - viewDur;
    }

    setViewBounds({ start: new Date(newStart), end: new Date(newEnd) });
  }, [getEventBounds, viewBounds]);

  const resetView = useCallback(() => {
    const bounds = getEventBounds();
    if (bounds) {
      setViewBounds({ start: bounds.start, end: bounds.end });
    }
  }, [getEventBounds]);

  const eventBounds = getEventBounds();
  const effectiveView = viewBounds ?? eventBounds;
  const eventDuration = eventBounds
    ? eventBounds.end.getTime() - eventBounds.start.getTime()
    : 0;
  const viewDuration = effectiveView
    ? effectiveView.end.getTime() - effectiveView.start.getTime()
    : 0;
  const zoomLevel = eventDuration > 0 ? eventDuration / viewDuration : 1;
  const canPanLeft = !!(
    eventBounds &&
    effectiveView &&
    effectiveView.start.getTime() > eventBounds.start.getTime()
  );
  const canPanRight = !!(
    eventBounds &&
    effectiveView &&
    effectiveView.end.getTime() < eventBounds.end.getTime()
  );

  return {
    viewBounds,
    zoomIn,
    zoomOut,
    panLeft,
    panRight,
    resetView,
    setViewBounds,
    canZoomIn: zoomLevel < MAX_ZOOM_LEVEL,
    canZoomOut: zoomLevel > MIN_ZOOM_LEVEL,
    canPanLeft,
    canPanRight,
    isZoomed: zoomLevel > MIN_ZOOM_LEVEL,
  };
}
